import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import AudioMixerService from './services/AudioMixer';
import WebRTCService from './services/WebRTC';
import SongLibrary from './components/SongLibrary';
import MusicSearch from './components/MusicSearch';
import './App.css';


const SERVER_URL = process.env.REACT_APP_SERVER_URL || window.location.origin;
function App() {
  const [socket, setSocket] = useState(null);
  const [roomId, setRoomId] = useState('');
  const [userName, setUserName] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [micEnabled, setMicEnabled] = useState(false);
  const [micVolume, setMicVolume] = useState(1.0);
  const [songVolume, setSongVolume] = useState(0.7);
  const [masterVolume, setMasterVolume] = useState(1.0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showMusicSearch, setShowMusicSearch] = useState(false);
  const [lyricsStatus, setLyricsStatus] = useState('idle');
  const [isLoadingSong, setIsLoadingSong] = useState(false); // État de chargement
  const [loadingProgress, setLoadingProgress] = useState({ ready: 0, total: 0 }); // ✨ NOUVEAU : Progression du chargement

  // Conserver les paroles locales quand currentSong est remplacé par un objet "sans paroles"
  // (ex: renvoi Socket.IO / bibliothèque) pour éviter que l'UI repasse à "Paroles indisponibles".
  const mergeSongKeepingLyrics = (prevSong, nextSong) => {
    if (!nextSong) return nextSong;
    if (!prevSong) return nextSong;

    const sameById = prevSong.id && nextSong.id && String(prevSong.id) === String(nextSong.id);
    const sameByUrl = prevSong.url && nextSong.url && String(prevSong.url) === String(nextSong.url);
    const same = sameById || sameByUrl;
    if (!same) return nextSong;

    const merged = { ...nextSong };
    if (!merged.lyrics && prevSong.lyrics) merged.lyrics = prevSong.lyrics;
    if (!merged.title && prevSong.title) merged.title = prevSong.title;
    if (!merged.artist && prevSong.artist) merged.artist = prevSong.artist;
    if (!merged.album && prevSong.album) merged.album = prevSong.album;
    if (!merged.duration && prevSong.duration) merged.duration = prevSong.duration;
    return merged;
  };
  
  const audioMixerRef = useRef(null);
  const webRTCRef = useRef(null);
  const audioElementRef = useRef(null);
  const animationFrameRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaContainerRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Refs d'état (utiles dans des callbacks sans dépendances)
  const isPlayingRef = useRef(false);
  const playbackRateRef = useRef(1.0);

  // --- Sync horloge avec le serveur (réduit le "petit décalage" entre participants) ---
  // serverTimeOffsetMsRef = (heureServeur - heureLocale) estimé via aller/retour (RTT/2)
  const serverTimeOffsetMsRef = useRef(0);
  const lastStartTimeMsRef = useRef(null);
  const timeSyncRef = useRef({ inProgress: false, remaining: 0, samples: [], nonce: null, t0: 0 });
  // Timers de planification (permet d'annuler un départ si on stop/pause avant startAt)
  const playScheduleRef = useRef({ playTimeout: null, unmuteTimeout: null });

  const getServerNowMs = () => Date.now() + (Number(serverTimeOffsetMsRef.current) || 0);


const isVideoUrl = (url = '') => {
  const clean = String(url).split('?')[0].toLowerCase();
  return clean.endsWith('.mp4') || clean.endsWith('.webm') || clean.endsWith('.mov') || clean.endsWith('.mkv') || clean.endsWith('.avi') || clean.endsWith('.m4v');
};

const mountMediaElement = (mediaEl) => {
        if (!mediaEl || !mediaContainerRef.current) return;
        try {
          // Nettoyer l'ancien media (si présent)
          mediaContainerRef.current.innerHTML = '';
          // N'afficher la zone que pour les vidéos
          const isVideo = String(mediaEl.tagName || '').toUpperCase() === 'VIDEO';
          mediaContainerRef.current.style.display = isVideo ? 'block' : 'none';

          // Styles de base (le CSS finit le travail)
          mediaEl.controls = false;
          mediaEl.playsInline = true;
          mediaEl.style.width = '100%';
          mediaEl.style.display = 'block';
          mediaContainerRef.current.appendChild(mediaEl);
        } catch (e) {
          console.warn('⚠️ Impossible de monter l’élément média:', e);
        }
      };

const deriveTitleArtist = (song) => {
  if (!song) return { title: '', artist: '' };
  let title = song.title || song.name || song.originalName || '';
  // Retirer extension si c’est un nom de fichier
  title = String(title).replace(/\.[a-z0-9]{2,5}$/i, '');
  let artist = song.artist || '';

  // Normaliser les séparateurs courants
  const base = String(title).replace(/_/g, ' ').trim();

  // Heuristique: "Artiste - Titre" (tirets - / – / —)
  if (!artist) {
    const m = base.match(/^(.+?)\s*[\-–—]\s*(.+)$/);
    if (m && m[1] && m[2]) {
      artist = m[1].trim();
      title = m[2].trim();
    }
  }

  // Heuristique: "Titre by Artiste"
  if (!artist) {
    const by = base.match(/^(.+?)\s+by\s+(.+)$/i);
    if (by && by[1] && by[2]) {
      title = by[1].trim();
      artist = by[2].trim();
    }
  }
  return { title: String(title).trim(), artist: String(artist).trim() };
};

  useEffect(() => {
    // Initialiser le mixer audio
    const initAudio = async () => {
      audioMixerRef.current = new AudioMixerService();
      await audioMixerRef.current.initialize();
    };
    
    initAudio();
    
    return () => {
      if (audioMixerRef.current) {
        audioMixerRef.current.cleanup();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Débloquer l'audio sur le premier geste utilisateur (Safari/iOS & autoplay policies)
  useEffect(() => {
    const unlock = async () => {
      if (audioUnlockedRef.current) return;
      try {
        await audioMixerRef.current?.ensureRunning?.();
        await audioMixerRef.current?.audioContext?.resume?.();
      } catch (e) {
        // Ignorer
      }
      audioUnlockedRef.current = true;
    };

    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  // Garder une version "ref" de isPlaying pour les callbacks (sans recréer d'intervalles)
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (isConnected && socket) {
      // Initialiser WebRTC
      webRTCRef.current = new WebRTCService(socket, audioMixerRef.current);
      webRTCRef.current.setCurrentRoomId(roomId);
      
      // Démarrer la visualisation audio
      startAudioVisualization();
    }
    
    return () => {
      if (webRTCRef.current) {
        webRTCRef.current.closeAllConnections();
      }
    };
  }, [isConnected, socket, roomId]);

  // --- Sync horloge (client <-> serveur) ---
  // Objectif: éviter le "petit décalage" dû aux horloges non alignées entre appareils.
  useEffect(() => {
    if (!socket) return;
    const sock = socket;
    let disposed = false;

    const sendSample = () => {
      if (disposed) return;
      if (!sock.connected) return;
      const nonce = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const t0 = Date.now();
      timeSyncRef.current.nonce = nonce;
      timeSyncRef.current.t0 = t0;
      sock.emit('sync-time', { nonce, t0 });
    };

    const finalize = () => {
      const samples = Array.isArray(timeSyncRef.current.samples) ? timeSyncRef.current.samples : [];
      if (!samples.length) {
        timeSyncRef.current.inProgress = false;
        return;
      }

      // On retient les meilleures mesures (RTT le plus faible) et on moyenne
      const sorted = [...samples].sort((a, b) => a.rtt - b.rtt);
      const best = sorted.slice(0, Math.min(3, sorted.length));
      const avgOffset = best.reduce((sum, s) => sum + s.offset, 0) / best.length;
      if (Number.isFinite(avgOffset)) {
        const prevOffset = Number(serverTimeOffsetMsRef.current) || 0;
        // Pendant la lecture, on évite les sauts d'offset qui peuvent créer des corrections brutales.
        // On lisse donc l'offset (EMA).
        const alpha = isPlayingRef.current ? 0.15 : 1.0;
        serverTimeOffsetMsRef.current = prevOffset + (avgOffset - prevOffset) * alpha;
      }

      const minRtt = Number.isFinite(best[0]?.rtt) ? best[0].rtt : null;
      console.log(
        `🕒 Sync horloge: offset≈${Number(serverTimeOffsetMsRef.current).toFixed(0)}ms` +
          (minRtt !== null ? ` (RTT min ~${minRtt}ms)` : '')
      );

      timeSyncRef.current.inProgress = false;
    };

    const onSyncTime = ({ nonce, t0, serverNow }) => {
      if (disposed) return;
      if (!timeSyncRef.current.inProgress) return;
      if (!nonce || nonce !== timeSyncRef.current.nonce) return;
      if (typeof t0 !== 'number' || typeof serverNow !== 'number') return;

      const t1 = Date.now();
      const rtt = Math.max(0, t1 - t0);
      // Estimation classique: serverNow + (RTT/2) correspond au temps serveur au moment où on reçoit.
      const offset = (serverNow + rtt / 2) - t1;

      timeSyncRef.current.samples.push({ rtt, offset });
      timeSyncRef.current.remaining = Math.max(0, (timeSyncRef.current.remaining || 0) - 1);

      if (timeSyncRef.current.remaining > 0) {
        setTimeout(sendSample, 120);
      } else {
        finalize();
      }
    };

    const run = (n = 8) => {
      if (disposed) return;
      if (timeSyncRef.current.inProgress) return;
      timeSyncRef.current.inProgress = true;
      timeSyncRef.current.remaining = n;
      timeSyncRef.current.samples = [];
      sendSample();
    };

    sock.on('sync-time', onSyncTime);

    // Sync rapide au démarrage, puis rafraîchissement périodique (stabilise les longues sessions)
    run(8);
    const interval = setInterval(() => run(5), 30000);

    return () => {
      disposed = true;
      clearInterval(interval);
      sock.off('sync-time', onSyncTime);
    };
  }, [socket]);

  // Correction douce du drift (évite les saccades)
  // - On évite les seeks fréquents (currentTime=...) qui créent des coupures audibles.
  // - On ajuste légèrement playbackRate pour recoller progressivement.
  // - On ne fait un "hard seek" qu'en cas de gros écart (rare).
  useEffect(() => {
    const timer = setInterval(() => {
      const el = audioElementRef.current;
      if (!el) return;
      if (!isPlayingRef.current) return;

      const st = lastStartTimeMsRef.current;
      if (!Number.isFinite(st)) return;

      const expected = (getServerNowMs() - st) / 1000;
      const actual = Number(el.currentTime) || 0;
      const diff = expected - actual;
      if (!Number.isFinite(diff)) return;

      // Gros décalage: on recale une fois (rare), puis on revient à 1.0
      if (Math.abs(diff) > 0.8) {
        try {
          const dur = Number(el.duration);
          const maxT = Number.isFinite(dur) ? Math.max(0, dur - 0.25) : expected;
          const target = Math.min(Math.max(0, expected), maxT);
          el.currentTime = target;
          el.playbackRate = 1.0;
          playbackRateRef.current = 1.0;
          console.log(`🔧 Hard-sync (rare) diff=${diff.toFixed(3)}s -> ${target.toFixed(2)}s`);
        } catch (e) {}
        return;
      }

      // Correction progressive via playbackRate
      // Objectif: corriger ~diff en ~4s (donc deltaRate ≈ diff/4)
      const desired = 1.0 + Math.max(-0.03, Math.min(0.03, diff / 4));
      const prev = Number(playbackRateRef.current) || 1.0;
      const smooth = prev * 0.7 + desired * 0.3;

      playbackRateRef.current = smooth;
      try {
        el.playbackRate = smooth;
      } catch (e) {}
    }, 500);

    return () => {
      clearInterval(timer);
      try {
        const el = audioElementRef.current;
        if (el) el.playbackRate = 1.0;
      } catch (e) {}
      playbackRateRef.current = 1.0;
    };
  }, []);


// Charger automatiquement les paroles (si on a au moins Titre + Artiste)
// ---- Paroles (LRCLib + cache local + cache serveur) ----
// Bug corrigé: quand on re-sélectionne la *même* chanson (ou qu'un événement Socket remplace currentSong),
// on peut perdre currentSong.lyrics (car l'objet reçu n'inclut pas les paroles).
// Comme les dépendances de l'effet n'incluaient pas la présence des paroles,
// React ne relançait pas la récupération.
useEffect(() => {
  let cancelled = false;

  const loadLyrics = async () => {
    if (!currentSong) {
      setLyricsStatus('idle');
      return;
    }

    if (currentSong.lyrics) {
      setLyricsStatus('loaded');
      return;
    }

    const { title, artist } = deriveTitleArtist(currentSong);
    if (!title) {
      setLyricsStatus('none');
      return;
    }

    // Cache local (évite les échecs intermittents au redémarrage)
    try {
      const cacheKey = artist
        ? `lyrics:${String(artist).trim().toLowerCase()}||${String(title).trim().toLowerCase()}`
        : `lyrics:q||${String(title).trim().toLowerCase()}`;
      const cachedRaw = window.localStorage.getItem(cacheKey);
      if (cachedRaw) {
        const cachedLyrics = JSON.parse(cachedRaw);
        setCurrentSong(prev => {
          if (!prev) return prev;
          if (prev.lyrics) return prev;
          return { ...prev, title, artist, lyrics: cachedLyrics };
        });
        setLyricsStatus('loaded');
        return;
      }
    } catch (e) {
      // ignore cache errors
    }


    setLyricsStatus('loading');
    try {
      const params = new URLSearchParams();
      params.set('title', title);
      if (artist) params.set('artist', artist);
      // Fallback: si artiste inconnu, on laisse le serveur interroger LRCLib via `q`.
      if (!artist) params.set('q', title);
      if (currentSong.album) params.set('album', currentSong.album);
      if (currentSong.duration) params.set('duration', String(Math.round(currentSong.duration)));

      const resp = await fetch(`${SERVER_URL}/api/lyrics?${params.toString()}`);
      if (!resp.ok) {
        if (!cancelled) setLyricsStatus('none');
        return;
      }
      const lyrics = await resp.json();
      if (cancelled) return;

      // Sauvegarder en cache local (clé différente si artiste manquant)
      try {
        const cacheKey = artist
          ? `lyrics:${String(artist).trim().toLowerCase()}||${String(title).trim().toLowerCase()}`
          : `lyrics:q||${String(title).trim().toLowerCase()}`;
        window.localStorage.setItem(cacheKey, JSON.stringify(lyrics));
      } catch (e) {
        // ignore
      }

      // LRCLib peut renvoyer le titre/artiste du meilleur match -> on l'utilise.
      const resolvedTitle = lyrics?.trackName ? String(lyrics.trackName).trim() : title;
      const resolvedArtist = lyrics?.artistName ? String(lyrics.artistName).trim() : artist;

      // Persister le couple (titre, artiste) côté serveur pour les prochains redémarrages
      try {
        if (currentSong?.id) {
          fetch(`${SERVER_URL}/api/songs/${currentSong.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ title: resolvedTitle, artist: resolvedArtist })
          }).catch(() => {});
        }
      } catch (e) {
        // ignore
      }

      setCurrentSong(prev => {
        if (!prev) return prev;
        if (prev.lyrics) return prev;
        return { ...prev, title: resolvedTitle, artist: resolvedArtist, lyrics };
      });
      setLyricsStatus('loaded');
    } catch (e) {
      console.warn('⚠️ Paroles indisponibles:', e);
      if (!cancelled) setLyricsStatus('error');
    }
  };

  loadLyrics();
  return () => { cancelled = true; };
// Inclure la présence des paroles: si un objet "sans paroles" remplace la chanson courante,
// l'effet se relance et recharge depuis cache/LRCLib.
}, [currentSong?.id, currentSong?.title, currentSong?.artist, currentSong?.url, !!currentSong?.lyrics]);

  const joinRoom = async () => {
    if (!roomId.trim() || !userName.trim()) {
      alert('Veuillez entrer un nom de room et un nom d\'utilisateur');
      return;
    }
    
    console.log('🔌 Tentative de connexion au serveur:', SERVER_URL);
    
    const newSocket = io(SERVER_URL);
    setSocket(newSocket);
    
    newSocket.on('connect', () => {
      console.log('✅ Connecté au serveur, Socket ID:', newSocket.id);
      newSocket.emit('join-room', { roomId, userName });
      console.log('📤 Événement join-room émis:', { roomId, userName });
    });
    
    newSocket.on('room-state', ({ participants: parts, currentSong: song, isPlaying: playing }) => {
      console.log('📥 room-state reçu:', { participants: parts.length, currentSong: song?.title || song?.name, isPlaying: playing });
      setParticipants(parts);
      setCurrentSong(prev => mergeSongKeepingLyrics(prev, song));
      setIsPlaying(playing);
      setIsConnected(true);
    });
    
    newSocket.on('participant-joined', ({ participants: parts }) => {
      console.log('👤 participant-joined reçu, total participants:', parts.length);
      setParticipants(parts);
      
      // Établir une connexion WebRTC avec le nouveau participant
      const newParticipant = parts[parts.length - 1];
      if (webRTCRef.current && newParticipant.id !== newSocket.id) {
        webRTCRef.current.createOffer(newParticipant.id, roomId);
      }
    });
    
    newSocket.on('participant-left', ({ participants: parts }) => {
      console.log('👋 participant-left reçu, participants restants:', parts.length);
      setParticipants(parts);
    });
    
    // ✨ NOUVEAU : Progression du chargement
    newSocket.on('loading-progress', ({ ready, total }) => {
      console.log(`📊 Progression: ${ready}/${total} participants prêts`);
      setLoadingProgress({ ready, total });
    });
    
    // ✨ NOUVEAU : Tous les participants sont prêts
    newSocket.on('all-participants-ready', () => {
      console.log('🎉 Tous les participants sont prêts !');
      setIsLoadingSong(false);
      setLoadingProgress({ ready: 0, total: 0 });
    });
    
    
    newSocket.on('song-selected', async (songData) => {
      console.log('📻 ========== SONG-SELECTED REÇU ==========');
      console.log('📻 Chanson sélectionnée par un autre participant:', songData);
      console.log('🔍 DEBUG - songData complète:', JSON.stringify(songData, null, 2));
      console.log('🔍 DEBUG - songData.url:', songData?.url);
      console.log('🔍 DEBUG - audioMixerRef.current existe?', !!audioMixerRef.current);
      console.log('📻 ==========================================');
      
      setCurrentSong(prev => mergeSongKeepingLyrics(prev, songData));
      setIsPlaying(false);
      setIsLoadingSong(true); // ✨ Début du chargement

      // ✨ NOUVEAU : Timeout de sécurité (30 secondes)
      const safetyTimeout = setTimeout(() => {
        console.warn('⏱️ Timeout de sécurité : déblocage après 30 secondes');
        setIsLoadingSong(false);
        setLoadingProgress({ ready: 0, total: 0 });
      }, 30000);

      // Charger automatiquement l'audio/vidéo pour tous les participants
      if (!songData) {
        console.error('❌ songData est null ou undefined');
        clearTimeout(safetyTimeout);
        setIsLoadingSong(false);
        return;
      }
      
      if (!songData.url) {
        console.error('❌ songData.url est manquant!', songData);
        alert('❌ La chanson sélectionnée n\'a pas d\'URL.\n\nProblème de synchronisation - actualisez la page.');
        clearTimeout(safetyTimeout);
        setIsLoadingSong(false);
        return;
      }
      
      console.log('🔊 Chargement automatique du média:', songData.url);
      
      try {
        let mediaEl = null;

        if (audioMixerRef.current) {
          console.log('📡 Utilisation de AudioMixer.loadSong()');
          mediaEl = await audioMixerRef.current.loadSong(songData.url);
          console.log('✅ AudioMixer.loadSong() terminé, mediaEl:', mediaEl?.tagName);
        } else {
          console.warn('⚠️ audioMixerRef.current est null, fallback simple');
          // Fallback simple (sans WebAudio)
          mediaEl = document.createElement(isVideoUrl(songData.url) ? 'video' : 'audio');
          mediaEl.crossOrigin = 'anonymous';
          mediaEl.preload = 'auto';
          mediaEl.src = songData.url;
          mediaEl.load();
          
          // Attendre que le média soit prêt
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.warn('⚠️ Timeout chargement média fallback');
              resolve();
            }, 10000);
            
            mediaEl.addEventListener('loadedmetadata', () => {
              clearTimeout(timeout);
              console.log('✅ Métadonnées chargées (fallback)');
              resolve();
            }, { once: true });
            
            mediaEl.addEventListener('error', (e) => {
              clearTimeout(timeout);
              console.error('❌ Erreur chargement média (fallback):', e);
              reject(e);
            }, { once: true });
          });
        }

        if (!mediaEl) {
          throw new Error('loadSong() a retourné null');
        }

        audioElementRef.current = mediaEl;
        mountMediaElement(mediaEl);
        console.log('✅ ========== MÉDIA CHARGÉ ET PRÊT ==========');
        console.log('✅ audioElementRef.current assigné:', audioElementRef.current?.tagName, audioElementRef.current?.src);
        console.log('✅ Durée:', audioElementRef.current?.duration);
        console.log('✅ ==========================================');
        
        // ✨ NOUVEAU : Signaler au serveur qu'on est prêt
        newSocket.emit('participant-ready', { roomId });
        console.log('📤 Signal participant-ready envoyé au serveur');
        
      } catch (error) {
        console.error('❌ ========== ERREUR CHARGEMENT ==========');
        console.error('❌ Erreur lors du chargement auto:', error);
        console.error('❌ Stack:', error.stack);
        console.error('❌ ==========================================');

        // Fallback ultime: créer quand même un élément média
        try {
          console.log('🔄 Tentative fallback ultime...');
          const mediaEl = document.createElement(isVideoUrl(songData.url) ? 'video' : 'audio');
          mediaEl.crossOrigin = 'anonymous';
          mediaEl.muted = false;
          mediaEl.defaultMuted = false;
          mediaEl.volume = 1.0;
          mediaEl.playsInline = true;
          mediaEl.setAttribute('playsinline', '');
          mediaEl.setAttribute('webkit-playsinline', 'true');
          mediaEl.preload = 'auto';
          mediaEl.src = songData.url;
          audioElementRef.current = mediaEl;
          mountMediaElement(mediaEl);
          console.log('✅ Fallback ultime réussi');
          
          // ✨ NOUVEAU : Signaler au serveur qu'on est prêt (même en fallback)
          newSocket.emit('participant-ready', { roomId });
          console.log('📤 Signal participant-ready envoyé au serveur (fallback)');
        } catch (fallbackError) {
          console.error('❌ Même le fallback ultime a échoué:', fallbackError);
          alert(`❌ Impossible de charger la chanson.\n\nURL: ${songData.url}\n\nErreur: ${error.message}`);
        }
      }
    });

    newSocket.on('song-play', async ({ startTime, startAt }) => {
      console.log('▶️ Lecture synchronisée demandée par un autre participant');
      console.log('🔍 DEBUG song-play - audioElementRef.current existe?', !!audioElementRef.current);
      console.log('🔍 DEBUG song-play - currentSong:', currentSong);

      // startTime = timestamp (ms) correspondant à la position 0.
      // startAt (optionnel) = instant serveur planifié pour démarrer (réduit fortement le décalage entre appareils).
      const startTimeMs = (typeof startTime === 'number' && Number.isFinite(startTime)) ? startTime : null;
      const startAtMs = (typeof startAt === 'number' && Number.isFinite(startAt)) ? startAt : null;

      // Mémoriser le startTime (temps serveur) pour les corrections de drift
      lastStartTimeMsRef.current = startTimeMs;

      // Vérifier si l'audio est chargé
      if (!audioElementRef.current) {
        console.error('❌ Aucun élément audio chargé pour la synchronisation');
        
        // Tentative de chargement en urgence si currentSong existe
        if (currentSong && currentSong.url && audioMixerRef.current) {
          console.log('🔄 Tentative de chargement d\'urgence de:', currentSong.url);
          try {
            const mediaEl = await audioMixerRef.current.loadSong(currentSong.url);
            audioElementRef.current = mediaEl;
            mountMediaElement(mediaEl);
            console.log('✅ Chargement d\'urgence réussi, on continue...');
            // Continuer vers la lecture ci-dessous
          } catch (err) {
            console.error('❌ Échec du chargement d\'urgence:', err);
            alert('❌ La chanson n\'est pas chargée.\n\nLe participant qui a lancé la musique doit la sélectionner à nouveau, ou actualisez votre page.');
            return;
          }
        } else {
          console.error('❌ Impossible de charger en urgence - pas de currentSong.url ou pas d\'audioMixer');
          alert('❌ La chanson n\'est pas chargée chez vous.\n\nActualisez la page et réessayez, ou sélectionnez la chanson depuis la bibliothèque.');
          return;
        }
      }

      // Reprendre le AudioContext si besoin
      try {
        if (audioMixerRef.current) {
          await audioMixerRef.current.ensureRunning();
        }
      } catch (e) {
        console.warn('⚠️ Impossible de reprendre AudioContext:', e);
      }

      // --- Planification de démarrage commun ---
      // Si startAt est présent, tous les clients démarrent au même instant (serveur).
      // Sinon, fallback: démarrer "maintenant" en se calant sur startTime.
      const clampToMedia = (tSec) => {
        const dur = Number(audioElementRef.current?.duration);
        const maxT = Number.isFinite(dur) ? Math.max(0, dur - 0.25) : Math.max(0, tSec);
        return Math.min(Math.max(0, tSec), maxT);
      };

      const syncHardNow = () => {
        if (!startTimeMs) return;
        try {
          const expected = (getServerNowMs() - startTimeMs) / 1000;
          const actual = Number(audioElementRef.current?.currentTime) || 0;
          const diff = expected - actual;
          if (Number.isFinite(diff) && Math.abs(diff) > 0.08) {
            const target = clampToMedia(expected);
            audioElementRef.current.currentTime = target;
            console.log(`🔧 Hard-sync ${diff.toFixed(3)}s -> ${target.toFixed(2)}s`);
          }
        } catch (e) {
          // ignore
        }
      };

      const serverNow = getServerNowMs();
      const effectiveStartAt = (startAtMs && startAtMs > 0) ? startAtMs : serverNow;
      let waitMs = Math.max(0, effectiveStartAt - serverNow);
      if (waitMs > 15000) waitMs = 15000;

      const targetAtStart = startTimeMs ? Math.max(0, (effectiveStartAt - startTimeMs) / 1000) : 0;
      // Pré-roll: on démarre en silence un peu avant startAt pour absorber la latence variable de play().
      const preRollMs = Math.min(650, waitMs, Math.floor(targetAtStart * 1000));
      const playInMs = Math.max(0, waitMs - preRollMs);
      const initialSeek = clampToMedia(targetAtStart - (preRollMs / 1000));

      // Attendre que la durée soit disponible (max 2s) pour éviter des seeks invalides
      if (!Number.isFinite(audioElementRef.current.duration)) {
        await new Promise(resolve => {
          let attempts = 0;
          const iv = setInterval(() => {
            attempts++;
            if (Number.isFinite(audioElementRef.current.duration) || attempts > 20) {
              clearInterval(iv);
              resolve();
            }
          }, 100);
        });
      }

      // Pré-seek au bon endroit
      try {
        audioElementRef.current.currentTime = initialSeek;
      } catch (e) {
        // ignore
      }

      // Reset playbackRate (important si une correction douce était en cours)
      try {
        audioElementRef.current.playbackRate = 1.0;
      } catch (e) {}
      playbackRateRef.current = 1.0;

      // Mute pendant la phase d'alignement (si possible)
      let restoreVolume = null;
      let usedGain = false;
      try {
        if (audioMixerRef.current?.songGain?.gain && Number.isFinite(audioMixerRef.current.songGain.gain.value)) {
          // Sécurité: certains navigateurs peuvent encore jouer l'élément en sortie directe
          // même quand il est routé via WebAudio. On mute donc *gain* + *volume*.
          restoreVolume = Number(audioElementRef.current.volume);
          audioElementRef.current.muted = false;
          audioElementRef.current.volume = 0;
          audioMixerRef.current.songGain.gain.value = 0;
          usedGain = true;
        } else {
          restoreVolume = Number(audioElementRef.current.volume);
          audioElementRef.current.muted = false;
          audioElementRef.current.volume = 0;
        }
      } catch (e) {
        // ignore
      }

      const unmute = () => {
        try {
          if (usedGain) {
            // Restaurer l'élément (au cas où il était encore audible en sortie directe)
            if (restoreVolume !== null && Number.isFinite(restoreVolume)) {
              audioElementRef.current.volume = restoreVolume;
            } else {
              audioElementRef.current.volume = 1.0;
            }
            audioMixerRef.current?.setSongVolume?.(songVolume);
            audioMixerRef.current?.setMasterVolume?.(masterVolume);
          } else if (restoreVolume !== null) {
            audioElementRef.current.muted = false;
            audioElementRef.current.volume = Math.max(0, Math.min(1, songVolume));
          }
        } catch (e) {}

      };

      const tryPlay = async () => {
        // Juste avant de jouer, on recalcule où on en est réellement
        syncHardNow();
        try {
          await audioElementRef.current.play();
          setIsPlaying(true);
          console.log('✅ Lecture synchronisée démarrée', { waitMs, preRollMs });
        } catch (error) {
          console.warn('⚠️ Lecture automatique bloquée par le navigateur:', error);

          // NOTIFICATION VISUELLE CLIQUABLE
          setIsPlaying(true);

          const notification = document.createElement('div');
          notification.id = 'karaoke-play-notification';
          notification.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 30px 40px;
            border-radius: 15px;
            box-shadow: 0 10px 40px rgba(0,0,0,0.5);
            z-index: 10000;
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            cursor: pointer;
            animation: pulseNotification 2s infinite;
            user-select: none;
          `;
          notification.innerHTML = `
            <div style="font-size: 48px; margin-bottom: 15px;">🎵</div>
            <div style="margin-bottom: 10px;">Un participant a lancé la musique !</div>
            <div style="font-size: 14px; opacity: 0.9;">👆 Cliquez ici pour lancer et synchroniser</div>
          `;

          const existingStyle = document.getElementById('karaoke-notification-style');
          if (!existingStyle) {
            const style = document.createElement('style');
            style.id = 'karaoke-notification-style';
            style.textContent = `
              @keyframes pulseNotification {
                0%, 100% { transform: translate(-50%, -50%) scale(1); }
                50% { transform: translate(-50%, -50%) scale(1.05); }
              }
            `;
            document.head.appendChild(style);
          }

          notification.onclick = async () => {
            try {
              syncHardNow();
              await audioElementRef.current.play();
              setIsPlaying(true);
              unmute();
              if (document.body.contains(notification)) document.body.removeChild(notification);
              console.log('✅ Lecture lancée par interaction utilisateur');
            } catch (err) {
              console.error('❌ Échec de la lecture:', err);
              alert('❌ Impossible de lire le média.\n\nErreur: ' + (err.message || 'Inconnue'));
            }
          };

          const oldNotification = document.getElementById('karaoke-play-notification');
          if (oldNotification) document.body.removeChild(oldNotification);
          document.body.appendChild(notification);

          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
              console.log('ℹ️ Notification expirée');
            }
          }, 15000);
        }
      };

      // Démarrer légèrement avant (pre-roll) puis unmute exactement au startAt.
      // On annule toute planification précédente pour éviter des "redémarrages fantômes".
      try {
        if (playScheduleRef.current.playTimeout) clearTimeout(playScheduleRef.current.playTimeout);
        if (playScheduleRef.current.unmuteTimeout) clearTimeout(playScheduleRef.current.unmuteTimeout);
      } catch (e) {}

      playScheduleRef.current.playTimeout = setTimeout(tryPlay, playInMs);
      playScheduleRef.current.unmuteTimeout = setTimeout(unmute, waitMs);
    });

    newSocket.on('song-pause', () => {
      try {
        if (playScheduleRef.current.playTimeout) clearTimeout(playScheduleRef.current.playTimeout);
        if (playScheduleRef.current.unmuteTimeout) clearTimeout(playScheduleRef.current.unmuteTimeout);
      } catch (e) {}
      setIsPlaying(false);
      lastStartTimeMsRef.current = null;
      if (audioElementRef.current) {
        audioElementRef.current.pause();
      }
    });
    
    newSocket.on('song-stop', () => {
      try {
        if (playScheduleRef.current.playTimeout) clearTimeout(playScheduleRef.current.playTimeout);
        if (playScheduleRef.current.unmuteTimeout) clearTimeout(playScheduleRef.current.unmuteTimeout);
      } catch (e) {}
      setIsPlaying(false);
      lastStartTimeMsRef.current = null;
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current.currentTime = 0;
      }
    });
    
    newSocket.on('recording-started', ({ recordingId }) => {
      console.log('🔴 Enregistrement démarré:', recordingId);
    });
    
    newSocket.on('recording-stopped', ({ filename, duration }) => {
      console.log('⏹️ Enregistrement terminé:', filename, duration);
    });
  };

  const leaveRoom = () => {
    if (socket) {
      socket.disconnect();
      setSocket(null);
    }
    
    if (audioMixerRef.current) {
      audioMixerRef.current.stopMicrophone();
    }
    
    setIsConnected(false);
    setParticipants([]);
    setCurrentSong(null);
    setIsPlaying(false);
    setMicEnabled(false);
  };

  const toggleMicrophone = async () => {
    if (!audioMixerRef.current) return;
    
    if (micEnabled) {
      audioMixerRef.current.stopMicrophone();
      setMicEnabled(false);
    } else {
      try {
        const stream = await audioMixerRef.current.startMicrophone();
        
        // Configurer WebRTC avec le stream
        if (webRTCRef.current) {
          await webRTCRef.current.setLocalStream(stream);
          
          // Établir des connexions avec tous les participants existants
          participants.forEach(participant => {
            if (participant.id !== socket.id) {
              webRTCRef.current.createOffer(participant.id, roomId);
            }
          });
        }
        
        setMicEnabled(true);
      } catch (error) {
        console.error('Erreur microphone détaillée:', error);
        
        let errorMessage = 'Impossible d\'accéder au microphone.\n\n';
        
        if (error.name === 'NotAllowedError') {
          errorMessage += '❌ Permission refusée.\n\nVeuillez autoriser l\'accès au microphone dans les paramètres de votre navigateur.';
        } else if (error.name === 'NotFoundError') {
          errorMessage += '❌ Aucun microphone détecté.\n\nVeuillez connecter un microphone et réessayer.';
        } else if (error.name === 'NotReadableError') {
          errorMessage += '❌ Microphone déjà utilisé.\n\nFermez les autres applications utilisant le microphone (Zoom, Teams, Discord, etc.)';
        } else if (error.name === 'OverconstrainedError') {
          errorMessage += '❌ Contraintes audio non supportées.\n\nVotre microphone ne supporte pas les paramètres demandés.';
        } else if (error.name === 'SecurityError') {
          errorMessage += '❌ Erreur de sécurité.\n\nAssurez-vous d\'utiliser HTTPS ou localhost.';
        } else {
          errorMessage += `❌ Erreur: ${error.message || error.name || 'Inconnue'}\n\nVérifiez les permissions du navigateur.`;
        }
        
        alert(errorMessage);
      }
    }
  };

  const handleSongUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log('❌ Aucun fichier sélectionné');
      return;
    }
    
    console.log('📁 Fichier sélectionné:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    console.log('📋 Type:', file.type);
    
    // Vérification taille uniquement
    const maxSize = 300 * 1024 * 1024; // 300 MB
    if (file.size > maxSize) {
      alert('❌ Fichier trop volumineux ! Maximum 300 MB.');
      event.target.value = '';
      return;
    }
    
    const formData = new FormData();
    formData.append('song', file);
    
    console.log('📤 Début de l\'upload vers:', `${SERVER_URL}/api/upload-song`);
    console.log('⏳ Cela peut prendre 1-2 minutes pour les gros fichiers...');
    
    try {
      const response = await fetch(`${SERVER_URL}/api/upload-song`, {
        method: 'POST',
        body: formData
      });
      
      console.log('📡 Réponse reçue:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        console.error('❌ Erreur serveur:', errorData);
        alert(`❌ Erreur d'upload:\n${errorData.error || 'Erreur inconnue'}`);
        event.target.value = '';
        return;
      }
      
      const songData = await response.json();
      console.log('✅ Données reçues du serveur:', songData);
      
      const fullSongData = {
        ...songData,
        url: `${SERVER_URL}${songData.path}`
      };
      
      console.log('🎵 URL complète de la chanson:', fullSongData.url);
      
      // Informer les autres participants
      if (socket) {
        socket.emit('select-song', { roomId, songData: fullSongData });
        setIsLoadingSong(true); // ✨ Attendre que les autres participants chargent
      }
      
      setCurrentSong(prev => mergeSongKeepingLyrics(prev, fullSongData));
      console.log('✅ currentSong mis à jour');
      
      // Charger la chanson dans le mixer
      if (audioMixerRef.current) {
        console.log('🔊 Chargement dans AudioMixer...');
        try {
          const audio = await audioMixerRef.current.loadSong(fullSongData.url);
          audioElementRef.current = audio;
          mountMediaElement(audio);
          // Appliquer les volumes UI au nouveau média chargé
          audioMixerRef.current.setSongVolume(songVolume);
          audioMixerRef.current.setMasterVolume(masterVolume);
          console.log('✅ Chanson chargée dans le mixer');
          
          // ✨ Note : On ne désactive plus isLoadingSong ici
          // C'est le serveur qui le fera via 'all-participants-ready'
          alert(`✅ Fichier chargé: ${songData.originalName}\n\nAttente des autres participants...`);
        } catch (loadError) {
          console.error('❌ Erreur lors du chargement:', loadError);
          setIsLoadingSong(false);
          alert(`⚠️ Fichier uploadé mais impossible de le lire automatiquement.\n\nFormat: ${file.type || 'inconnu'}\n\nSi c'est un format audio/vidéo supporté par votre navigateur, il devrait apparaître dans la bibliothèque locale.\n\nEssayez de le sélectionner depuis la bibliothèque.`);
        }
      } else {
        console.error('❌ AudioMixer non initialisé');
        setIsLoadingSong(false);
        alert('⚠️ Fichier uploadé avec succès.\n\nVous pouvez le trouver dans la bibliothèque locale.');
      }
      
      event.target.value = '';
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      alert(`❌ Erreur: ${error.message || 'Impossible de contacter le serveur'}\n\nVérifiez que le serveur est démarré.`);
      event.target.value = '';
    }
  };
  const handleSongSelectedFromLibrary = async (songData) => {
    console.log('🎵 Chanson sélectionnée:', songData);
    setCurrentSong(prev => mergeSongKeepingLyrics(prev, songData));
    setShowLibrary(false);
    setShowMusicSearch(false); // Fermer aussi la recherche si ouverte
    
    // Informer les autres participants via Socket.IO
    if (socket) {
      socket.emit('select-song', { roomId, songData });
      console.log('📡 Événement select-song émis pour synchroniser les participants');
      setIsLoadingSong(true); // ✨ Attendre que les autres participants chargent
    }
    
    // Charger la chanson dans le mixer
    if (audioMixerRef.current) {
      try {
        const audio = await audioMixerRef.current.loadSong(songData.url);
        audioElementRef.current = audio;
        mountMediaElement(audio);
        // Appliquer les volumes UI au nouveau média chargé
        audioMixerRef.current.setSongVolume(songVolume);
        audioMixerRef.current.setMasterVolume(masterVolume);
        console.log('✅ Chanson chargée localement');
        
        // ✨ Note : On ne désactive plus isLoadingSong ici
        // C'est le serveur qui le fera via 'all-participants-ready'
      } catch (error) {
        console.error('❌ Erreur chargement:', error);
        setIsLoadingSong(false);
        // Fallback
        const audio = new Audio(songData.url);
        audio.crossOrigin = 'anonymous';
        audio.volume = 1.0;
        audioElementRef.current = audio;
      }
    }
  };


  const playSong = async () => {
    console.log('▶️ Lecture...');

    if (!audioElementRef.current) {
      console.error('❌ Aucun média chargé');
      // Attention: utiliser des guillemets doubles ici à cause de l'apostrophe dans "d'abord"
      alert("❌ Aucune chanson chargée. Chargez d'abord un fichier.");
      return;
    }

    // S'assurer qu'il est monté (utile si on a changé de chanson)
    mountMediaElement(audioElementRef.current);

    // Débloquer / reprendre le AudioContext (nécessaire sur Chrome/Safari/iOS)
    try {
      await audioMixerRef.current?.ensureRunning?.();
      await audioMixerRef.current?.audioContext?.resume?.();
      audioUnlockedRef.current = true;
    } catch (e) {
      // Ignorer
    }

    // S'assurer que le média n'est pas muet
    try {
      audioElementRef.current.muted = false;
      audioElementRef.current.volume = 1.0;
    } catch (e) {
      // Ignorer
    }

// Connecter l'élément au mixer *dans le geste utilisateur* (important pour Safari/iOS)
// Si l'AudioContext ne peut pas démarrer (policy/autoplay), on bascule en sortie directe de l'élément.
let attachedToMixer = false;
try {
  attachedToMixer = await audioMixerRef.current?.attachMediaElement?.(audioElementRef.current);
  if (attachedToMixer) {
    // Réappliquer les volumes UI après connexion
    audioMixerRef.current?.setSongVolume?.(songVolume);
    audioMixerRef.current?.setMasterVolume?.(masterVolume);
  }
} catch (e) {
  console.warn('⚠️ Impossible de connecter le média au mixer (fallback direct):', e);
  attachedToMixer = false;
}

if (!attachedToMixer) {
  // Fallback : volume directement sur l'élément (le slider "Song" devient le volume média)
  try {
    audioElementRef.current.muted = false;
    audioElementRef.current.volume = Math.max(0, Math.min(1, songVolume));
  } catch (e) {}
}

    try {
      // Assigner des handlers simples
      audioElementRef.current.onended = () => {
        console.log('🎵 Chanson terminée');
        setIsPlaying(false);
      };
      audioElementRef.current.onerror = (e) => {
        console.error('❌ Erreur média pendant la lecture:', e);
        setIsPlaying(false);
      };

      // ✅ IMPORTANT: ne pas démarrer localement tout de suite.
      // On demande au serveur de planifier un départ commun (startAt) pour éviter
      // le décalage audible entre appareils.
      if (socket) {
        const currentTime = Number(audioElementRef.current?.currentTime) || 0;
        socket.emit('play-song', { roomId, currentTime });
        console.log('📡 Demande de lecture envoyée (démarrage synchronisé)…');
      } else {
        // Fallback hors room
        await audioElementRef.current.play();
        setIsPlaying(true);
        console.log('✅ Lecture démarrée (fallback)');
      }
    } catch (err) {
      console.error('❌ Erreur lors de la lecture:', err);
      setIsPlaying(false);

      if (err && err.name === 'NotAllowedError') {
        alert("❌ Le navigateur bloque la lecture automatique.\n\nCliquez d'abord n'importe où sur la page, puis réessayez.");
      } else if (err && err.name === 'NotSupportedError') {
        alert('❌ Format non supporté par le navigateur.\n\nEssayez MP3 (audio) ou MP4/WebM (vidéo).');
      } else {
        alert(`❌ Impossible de lire: ${err?.message || 'Erreur inconnue'}`);
      }
    }
  };

  const pauseSong = () => {
    console.log('⏸️ Pause...');
    
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      setIsPlaying(false);
      console.log('✅ Pause effectuée');
    }
    
    if (socket) {
      // Mémoriser la position côté serveur (utile pour les nouveaux arrivants / reprise cohérente)
      const currentTime = Number(audioElementRef.current?.currentTime) || 0;
      socket.emit('pause-song', { roomId, currentTime });
    }
  };

  const stopSong = () => {
    console.log('⏹️ Stop...');
    
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
      setIsPlaying(false);
      console.log('✅ Stop effectué');
    }
    
    if (socket) {
      socket.emit('stop-song', { roomId });
    }
  };

  const testAudio = async () => {
    try {
      const ok = await audioMixerRef.current?.testTone?.();
      if (!ok) {
        alert("❌ Le navigateur n'autorise pas la sortie audio (AudioContext bloqué).\n\nCliquez une fois sur la page puis réessayez, ou utilisez un fichier MP4 avec audio AAC.");
      }
    } catch (e) {
      console.warn('Test audio échoué:', e);
      alert("❌ Test audio échoué. Ouvrez la console pour voir le détail.");
    }
  };


  const startRecording = async () => {
    if (audioMixerRef.current) {
      const success = await audioMixerRef.current.startRecording();
      if (success) {
        setIsRecording(true);
        if (socket) {
          socket.emit('start-recording', { roomId });
        }
      }
    }
  };

  const stopRecording = async () => {
    if (audioMixerRef.current) {
      try {
        const blob = await audioMixerRef.current.stopRecording();
        setIsRecording(false);
        
        // Télécharger l'enregistrement
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `karaoke-${Date.now()}.webm`;
        a.click();
        
        if (socket) {
          socket.emit('stop-recording', { recordingId: Date.now() });
        }
      } catch (error) {
        console.error('Erreur lors de l\'arrêt de l\'enregistrement:', error);
      }
    }
  };

  const handleMicVolumeChange = (value) => {
    const volume = parseFloat(value);
    setMicVolume(volume);
    if (audioMixerRef.current) {
      audioMixerRef.current.setMicrophoneVolume(volume);
    }
  };

  const handleSongVolumeChange = (value) => {
    const volume = parseFloat(value);
    setSongVolume(volume);
    if (audioMixerRef.current) {
      audioMixerRef.current.setSongVolume(volume);
    }
  };

  const handleMasterVolumeChange = (value) => {
    const volume = parseFloat(value);
    setMasterVolume(volume);
    if (audioMixerRef.current) {
      audioMixerRef.current.setMasterVolume(volume);
    }
  };

  const startAudioVisualization = () => {
    const canvas = canvasRef.current;
    if (!canvas || !audioMixerRef.current) return;
    
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    const draw = () => {
      const dataArray = audioMixerRef.current.getFrequencyData();
      
      if (dataArray) {
        ctx.fillStyle = 'rgb(10, 10, 20)';
        ctx.fillRect(0, 0, width, height);
        
        const barWidth = (width / dataArray.length) * 2.5;
        let barHeight;
        let x = 0;
        
        for (let i = 0; i < dataArray.length; i++) {
          barHeight = (dataArray[i] / 255) * height * 0.8;
          
          const hue = (i / dataArray.length) * 360;
          ctx.fillStyle = `hsl(${hue}, 100%, 50%)`;
          ctx.fillRect(x, height - barHeight, barWidth, barHeight);
          
          x += barWidth + 1;
        }
        
        // Calculer le niveau audio moyen
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average / 255);
      }
      
      animationFrameRef.current = requestAnimationFrame(draw);
    };
    
    draw();
  };

  if (!isConnected) {
    return (
      <div className="app login-screen">
        <div className="login-container">
          <h1>🎤 Karaoké Collaboratif Pro</h1>
          <p className="subtitle">Chantez ensemble en temps réel avec mixage audio professionnel</p>
          
          <div className="login-form">
            <input
              type="text"
              placeholder="Nom de la room"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="input-field"
            />
            <input
              type="text"
              placeholder="Votre nom"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              className="input-field"
            />
            <button onClick={joinRoom} className="btn btn-primary">
              Rejoindre
            </button>
          </div>
          
          <div className="features">
            <div className="feature">
              <span className="icon">🎙️</span>
              <p>Micro en temps réel</p>
            </div>
            <div className="feature">
              <span className="icon">🎵</span>
              <p>Pistes instrumentales</p>
            </div>
            <div className="feature">
              <span className="icon">🎚️</span>
              <p>Mixage professionnel</p>
            </div>
            <div className="feature">
              <span className="icon">💾</span>
              <p>Enregistrement HD</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app main-screen">
      <header className="header">
        <div className="header-left">
          <h1>🎤 Karaoké Pro</h1>
          <span className="room-info">Room: {roomId}</span>
        </div>
        <div className="header-right">
          <span className="user-name">{userName}</span>
          <button onClick={leaveRoom} className="btn btn-danger btn-sm">
            Quitter
          </button>
        </div>
      </header>
      
      <div className="main-content">
        <div className="left-panel">
          <div className="section participants-section">
            <h3>👥 Participants ({participants.length})</h3>
            <div className="participants-list">
              {participants.map(participant => (
                <div key={participant.id} className="participant-item">
                  <div className="participant-info">
                    <span className="participant-name">
                      {participant.name}
                      {participant.id === socket?.id && ' (Vous)'}
                    </span>
                  </div>
                  <div className="participant-controls">
                    <span className={`status ${participant.isMuted ? 'muted' : 'active'}`}>
                      {participant.isMuted ? '🔇' : '🔊'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="section controls-section">
            <h3>🎚️ Contrôles Audio</h3>
            
            <div className="control-group">
              <label>
                <span>🎤 Microphone</span>
                <button 
                  onClick={toggleMicrophone} 
                  className={`btn ${micEnabled ? 'btn-danger' : 'btn-primary'} btn-block`}
                >
                  {micEnabled ? 'Désactiver' : 'Activer'}
                </button>
              </label>
              {micEnabled && (
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={micVolume}
                  onChange={(e) => handleMicVolumeChange(e.target.value)}
                  className="slider"
                />
              )}
            </div>
            
            <div className="control-group">
              <label>
                <span>🎵 Volume Musique</span>
                <span className="value">{Math.round(songVolume * 100)}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={songVolume}
                onChange={(e) => handleSongVolumeChange(e.target.value)}
                className="slider"
              />
            </div>
            
            <div className="control-group">
              <label>
                <span>🔊 Volume Master</span>
                <span className="value">{Math.round(masterVolume * 100)}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={masterVolume}
                onChange={(e) => handleMasterVolumeChange(e.target.value)}
                className="slider"
              />
            </div>
          </div>
        </div>
        
        <div className="center-panel">
          <div className="section visualizer-section">
            <div ref={mediaContainerRef} className="media-container" />
            <canvas ref={canvasRef} width="800" height="200" className="visualizer-canvas" />
            <div className="audio-level-bar">
              <div 
                className="audio-level-fill" 
                style={{ width: `${audioLevel * 100}%` }}
              />
            </div>
          </div>
          
          <div className="section song-section">
            <h3>🎵 Chanson</h3>
            {currentSong ? (
              <div className="song-info">
                <p className="song-name">{currentSong.title || currentSong.originalName || currentSong.name}</p>
                {currentSong.artist && <p className="song-artist">🎤 {currentSong.artist}</p>}
                {currentSong.duration && <p className="song-duration">⏱️ {Math.floor(currentSong.duration / 60)}:{String(currentSong.duration % 60).padStart(2, '0')}</p>}
                
                <div className="playback-controls">
                  {isLoadingSong && (
                    <div style={{
                      textAlign: 'center',
                      padding: '15px',
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))',
                      borderRadius: '10px',
                      marginBottom: '15px',
                      color: '#667eea',
                      fontWeight: '600'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                      <div>Chargement en cours...</div>
                      {loadingProgress.total > 0 && (
                        <div style={{ 
                          marginTop: '10px', 
                          fontSize: '14px',
                          color: '#764ba2'
                        }}>
                          {loadingProgress.ready}/{loadingProgress.total} participants prêts
                        </div>
                      )}
                      <div style={{
                        marginTop: '10px',
                        height: '4px',
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '2px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          background: 'linear-gradient(90deg, #667eea, #764ba2)',
                          width: loadingProgress.total > 0 
                            ? `${(loadingProgress.ready / loadingProgress.total) * 100}%` 
                            : '0%',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  )}
                  {!isPlaying ? (
                    <button 
                      onClick={playSong} 
                      className="btn btn-success"
                      disabled={isLoadingSong}
                      style={{
                        opacity: isLoadingSong ? 0.5 : 1,
                        cursor: isLoadingSong ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isLoadingSong ? '⏳ Chargement...' : '▶️ Lecture'}
                    </button>
                  ) : (
                    <button onClick={pauseSong} className="btn btn-warning">
                      ⏸️ Pause
                    </button>
                  )}
                  <button onClick={stopSong} className="btn btn-danger">
                    ⏹️ Stop
                  </button>
                  <button onClick={testAudio} className="btn btn-secondary" title="Joue un bip de test via WebAudio">
                    🔈 Test son
                  </button>
                </div>
                
                
{/* Paroles */}
<div className="lyrics-section" style={{marginTop: '20px', maxHeight: '300px', overflowY: 'auto', background: 'rgba(0,0,0,0.3)', padding: '15px', borderRadius: '10px'}}>
  <h4 style={{margin: '0 0 10px 0', color: '#667eea'}}>📝 Paroles</h4>
  {lyricsStatus === 'loading' ? (
    <p style={{opacity: 0.8, margin: 0}}>Chargement des paroles...</p>
  ) : currentSong.lyrics ? (
    <pre style={{whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9rem', lineHeight: '1.6', margin: 0}}>
      {currentSong.lyrics.plainLyrics || currentSong.lyrics.syncedLyrics || 'Paroles non disponibles'}
    </pre>
  ) : lyricsStatus === 'none' ? (
    <p style={{opacity: 0.8, margin: 0}}>
      Paroles non trouvées (ou titre manquant).<br/>
      Astuce: si c'est un fichier local, un nom du type <em>Artiste - Titre</em> aide à trouver de meilleurs résultats.
    </p>
  ) : lyricsStatus === 'error' ? (
    <p style={{opacity: 0.8, margin: 0}}>
      Erreur lors de la récupération des paroles (connexion Internet ?).
    </p>
  ) : (
    <p style={{opacity: 0.8, margin: 0}}>Paroles indisponibles.</p>
  )}
</div>
</div>
            ) : (
              <div className="no-song">
                <p>Aucune chanson sélectionnée</p>
              </div>
            )}
            
            <div className="song-actions">
              <button 
                onClick={() => setShowLibrary(!showLibrary)} 
                className="btn btn-primary"
              >
                {showLibrary ? '📂 Fermer la bibliothèque' : '🔍 Bibliothèque locale'}
              </button>
              
              <label htmlFor="song-upload" className="btn btn-primary btn-upload">
                📁 Charger un fichier
              </label>
              <input
                id="song-upload"
                type="file"
                onChange={handleSongUpload}
                style={{ display: 'none' }}
              />
            </div>
            
            {showLibrary && (
              <div className="library-container">
                <SongLibrary 
                  socket={socket}
                  roomId={roomId}
                  onSongSelected={handleSongSelectedFromLibrary}
                  serverUrl={SERVER_URL}
                />
              </div>
            )}
          </div>
          
          <div className="section recording-section">
            <h3>💾 Enregistrement</h3>
            <div className="recording-controls">
              {!isRecording ? (
                <button onClick={startRecording} className="btn btn-danger btn-large">
                  🔴 Démarrer l'enregistrement
                </button>
              ) : (
                <button onClick={stopRecording} className="btn btn-success btn-large">
                  ⏹️ Arrêter et télécharger
                </button>
              )}
            </div>
            {isRecording && (
              <div className="recording-indicator">
                <span className="recording-dot"></span>
                Enregistrement en cours...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
